import React from "react";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

type BackgroundShiftDetails = {
    shift_amount_x?: number;
};

const parseBackgroundShiftDetails = (
    backgroundShift: string | undefined,
): BackgroundShiftDetails => {
    if (!backgroundShift) {
        return {};
    }
    let parsed: unknown;
    try {
        parsed = JSON.parse(backgroundShift);
    } catch {
        return {};
    }
    if (!parsed || typeof parsed !== "object") {
        return {};
    }
    const shiftAmountX = (parsed as { shift_amount_x?: unknown })
        .shift_amount_x;
    if (typeof shiftAmountX !== "number") {
        return {};
    }
    return { shift_amount_x: shiftAmountX };
};

const shiftBackgroundImage = (
    elem: HTMLElement,
    shiftDetails: BackgroundShiftDetails,
): void => {
    const xShift = shiftDetails.shift_amount_x;
    if (xShift) {
        elem.style.backgroundPositionX = `${xShift}%`;
    }
};

const markLazyBackgroundVisible = (elem: HTMLElement): void => {
    elem.classList.add("lazy-background--visible");
    const backgroundImage = elem.dataset.backgroundImage;
    const backgroundShift = elem.dataset.backgroundImageShift;

    if (backgroundImage) {
        elem.style.backgroundImage = `url(${backgroundImage})`;
        const shiftDetails = parseBackgroundShiftDetails(backgroundShift);
        shiftBackgroundImage(elem, shiftDetails);
    }
    if (elem.classList.contains("background--image-full-height")) {
        // `minHeight`, not `height`: a full-height block must be *at least*
        // as tall as its background image but still grow to fit content
        // taller than the image. A fixed `height` clips overflowing content.
        // The legacy tsi-tempurpedic lazyloading.ts used `minHeight` here;
        // the other sites' fixed-`height` behavior was the less-correct one
        // that this shared hook had inadvertently standardized on (#33285).
        elem.style.minHeight = `${elem.dataset.backgroundImageHeight}px`;
    }
};

export const useLazyBackground = (
    ref: React.RefObject<HTMLElement | null>,
): void => {
    React.useEffect(() => {
        const elem = ref.current;
        const window = getWindow();
        if (!elem || !window || !elem.classList.contains("lazy-background")) {
            return;
        }
        if (window.navigator.userAgent.includes("jsdom")) {
            return;
        }
        if (
            "IntersectionObserver" in window &&
            !window.navigator.userAgent.includes("HeadlessChrome")
        ) {
            const observer = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        if (!(entry.target instanceof HTMLElement)) return;
                        markLazyBackgroundVisible(entry.target);
                        observer.unobserve(entry.target);
                    }
                });
            });
            observer.observe(elem);
            return () => {
                observer.disconnect();
            };
        }
        markLazyBackgroundVisible(elem);
    }, [ref]);
};
